import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { cloneDeep, identity, isEqual, map, omit, pickBy, slice } from 'lodash';
import { catchError, combineLatest, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { AbstractControlOptions, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Component, OnInit, ElementRef, Renderer2, ChangeDetectorRef, OnDestroy, PLATFORM_ID, Inject } from '@angular/core';

import { IPagination } from '@diktup/frontend/models';
import { ConvertorHelper, FormHelper, StorageHelper, ValidationHelper } from '@diktup/frontend/helpers';
import {
  UserType,
  StateType,
  OrderStatus,
  CountryType,
  FullAddressType,
  OrderSettingsFullType,
  MarketPlaceOrderDtoType,
} from '@sifca-monorepo/ecommerce-generator';

import { OrdersService } from '../../services/order/order/order.service';
import { AppService, AuthService, PosService, SeoService, SharedService, UserService } from '../../services';
import { TranslateService } from '@ngx-translate/core';

@Component({
  template: '',
})
export class BaseAccountComponent implements OnInit, OnDestroy {
  protected unsubscribeAll: Subject<any> = new Subject<any>();

  step = 1;
  seo: any;
  field: string;
  initValues: any;
  perPage: number;
  showAlert = false;
  initialValues: any;
  totalCount: number;
  userForm: FormGroup;
  states: StateType[];
  initialUserForm: any;
  selectedIndex: number;
  currentUser: UserType;
  billingForm: FormGroup;
  passwordForm: FormGroup;
  shippingForm: FormGroup;
  isButtonDisabled = true;
  pagination: IPagination;
  isLaodingOrders: boolean;
  allCountries: CountryType[];
  isShipButtonDisabled = true;
  isBillButtonDisabled = true;
  isPwdButtonDisabled: boolean;
  cords: { lng: any; lat: any };
  order: MarketPlaceOrderDtoType;
  billingAddress: FullAddressType[];
  selectedShipAddresses: any[] = [];
  shippingAddress: FullAddressType[];
  selectedShipAddress: FullAddressType;
  selectedBillAddress: FullAddressType;
  loadingUser: boolean;
  orderSettings: OrderSettingsFullType;
  selectedOrder: MarketPlaceOrderDtoType;
  selectedBillAddresses: FullAddressType[] = [];
  orders$: Observable<MarketPlaceOrderDtoType[]>;

  alert: { message: string } = {
    message: '',
  };

  constructor(
    protected el: ElementRef,
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected modalServices: NgbModal,
    protected seoService: SeoService,
    protected posService: PosService,
    protected titleTagService: Title,
    protected appService: AppService,
    protected userService: UserService,
    protected formBuilder: FormBuilder,
    protected authService: AuthService,
    protected activeRoute: ActivatedRoute,
    protected storageHelper: StorageHelper,
    protected orderService: OrdersService,
    protected translate: TranslateService,
    protected toastrService: ToastrService,
    protected sharedService: SharedService,
    protected convertorHelper: ConvertorHelper,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(PLATFORM_ID) protected platformId: Object,
  ) {
    this.orders$ = this.orderService.orders$;
    this.seoService
      .getGeneralSEOJSON()
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.seo = res;
        if (this.seo?.title) {
          this.titleTagService.setTitle(this.seo?.title);
        }
        this.sharedService.tags$ = [...this.metaTagService.addTags(map(this.seo?.metas, (meta) => pickBy(meta, identity))), { charset: 'UTF-8' }];
          this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
            const link = this.renderer.createElement('link');
            if (seoLink.sizes) {
              link.sizes = seoLink.sizes;
            }
            if (seoLink.href) {
              link.href = seoLink.href;
            }
            if (seoLink.rel) {
              link.rel = seoLink.rel;
            }
            this.renderer.appendChild(this.document.head, link);
            return link;
          });
      });
    this.perPage = this.orderService.orderLimit;
    this.orderService.order$.pipe(takeUntil(this.unsubscribeAll)).subscribe((order) => {
      this.order = order;
      this.changeDetectorRef.markForCheck();
    });
    this.appService.pagination$.pipe(takeUntil(this.unsubscribeAll)).subscribe((pagination: any) => {
      this.pagination = pagination;
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnInit(): void {
    combineLatest([this.orderService.getOrderSettings(), this.userService.getCurrentUser()])
      .pipe(
        catchError(() => {
          this.loadingUser = false;
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.loadingUser = false;
        }
      });
    this.passwordForm = this.formBuilder.group({
      pwd: this.formBuilder.group(
        {
          currentPassword: ['', Validators.required],
          password: ['', Validators.required],
          confirmPassword: ['', Validators.required],
        },
        { validators: [ValidationHelper.matchingFields('password', 'confirmPassword')] } as AbstractControlOptions,
      ),
    });
    const initVal = this.passwordForm.value;
    this.passwordForm.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((ivalues) => {
      this.isPwdButtonDisabled = isEqual(initVal, ivalues);
    });
    combineLatest([this.orderService.orderSettings$, this.userService.currentUser$])
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(([orderSettings, currentUser]) => {
        this.orderSettings = orderSettings;
        if (currentUser) {
          this.currentUser = currentUser;
          this.selectedBillAddresses = cloneDeep(this.currentUser?.billingAddress);
          this.selectedShipAddresses = cloneDeep(this.currentUser?.shippingAddress);
          this.loadingUser = false;
          this.userForm = this.formBuilder.group({
            username: [currentUser?.username],
            firstName: [currentUser?.firstName || '', [Validators.required]],
            lastName: [currentUser?.lastName || '', Validators.required],
            email: [currentUser?.email || '', [Validators.required, Validators.email]],
            phone: this.formBuilder.group(
              {
                number: [currentUser?.phone?.number || '', Validators.required],
                countryCode: [orderSettings?.deliveryCountries?.[0]?.dialCode || '', Validators.required],
              },
              { validators: [Validators.required] },
            ),
          });
          this.billingAddress = currentUser.billingAddress;
          this.shippingAddress = currentUser.shippingAddress;
          this.initialUserForm = this.userForm.value;
          this.userForm.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((values) => {
            this.isButtonDisabled = isEqual(values, this.initialUserForm);
          });
          this.changeDetectorRef.markForCheck();
        }
      });
    this.activeRoute.queryParams.subscribe((params) => {
      if (params['page']) {
        this.orderService.orderPage = params['page'] - 1;
      }
      this.findMarketPlaceOrdersByUserAndTarget();
    });
  }

  saveShippingAddress() {
    let input: any;
    let shippingAddress;
    this.isBillButtonDisabled = true;
    if (this.selectedShipAddress) {
      this.selectedShipAddresses[this.selectedIndex] = this.shippingForm.value;
      shippingAddress = [
        ...map(this.selectedShipAddresses, (address) => {
          return {
            ...(address?.country ? { country: address?.country?.id || address?.country } : {}),
            ...(address?.state ? { state: address?.state?.id || address?.state } : {}),
            ...(address?.city ? { city: address?.city } : {}),
            ...(address?.address ? { address: address?.address } : {}),
            ...(address?.postCode ? { postCode: address?.postCode } : {}),
            ...(address?.owner?.name
              ? {
                  owner: {
                    name: address?.owner?.name,
                    ...(address?.owner?.phone ? { phone: address?.owner?.phone } : {}),
                  },
                }
              : {}),
          };
        }),
      ];
    } else {
      shippingAddress = [
        {
          ...(this.shippingForm.value.country ? { country: this.shippingForm.value.country } : {}),
          ...(this.shippingForm.value.state ? { state: this.shippingForm.value.state } : {}),
          ...(this.shippingForm.value.city ? { city: this.shippingForm.value.city } : {}),
          ...(this.shippingForm.value.address ? { address: this.shippingForm.value.address } : {}),
          ...(this.shippingForm.value.postCode ? { postCode: this.shippingForm.value.postCode } : {}),
          ...(this.shippingForm.value.owner?.name
            ? {
                owner: {
                  name: this.shippingForm.value?.owner?.name,
                  ...(this.shippingForm.value.owner?.phone ? { phone: this.shippingForm.value?.owner?.phone } : {}),
                },
              }
            : {}),
        },
        ...map(this.selectedShipAddresses, (address) => {
          return {
            ...(address?.country ? { country: address?.country?.id } : {}),
            ...(address?.state ? { state: address?.state?.id } : {}),
            ...(address?.city ? { city: address?.city } : {}),
            ...(address?.address ? { address: address?.address } : {}),
            ...(address?.postCode ? { postCode: address?.postCode } : {}),
            ...(address?.owner?.name
              ? {
                  owner: {
                    name: address?.owner?.name,
                    ...(address?.owner?.phone ? { phone: address?.owner?.phone } : {}),
                  },
                }
              : {}),
          };
        }),
      ];
    }
    input = {
      shippingAddress,
    };
    this.updateCurrentUser(input);
  }

  saveBillingAddress() {
    let input: any;
    let billingAddress;
    this.isBillButtonDisabled = true;
    if (this.selectedBillAddress) {
      this.selectedBillAddresses[this.selectedIndex] = this.billingForm.value;
      billingAddress = [
        ...map(this.selectedBillAddresses, (address) => {
          return {
            ...(address?.country ? { country: address?.country?.id || address?.country } : {}),
            ...(address?.state ? { state: address?.state?.id || address?.state } : {}),
            ...(address?.city ? { city: address?.city } : {}),
            ...(address?.address ? { address: address?.address } : {}),
            ...(address?.postCode ? { postCode: address?.postCode } : {}),
            ...(address?.owner?.name
              ? {
                  owner: {
                    name: address?.owner?.name,
                    ...(address?.owner?.phone ? { phone: address?.owner?.phone } : {}),
                  },
                }
              : {}),
          };
        }),
      ];
    } else {
      billingAddress = [
        {
          ...(this.billingForm.value.country ? { country: this.billingForm.value.country } : {}),
          ...(this.billingForm.value.state ? { state: this.billingForm.value.state } : {}),
          ...(this.billingForm.value.city ? { city: this.billingForm.value.city } : {}),
          ...(this.billingForm.value.address ? { address: this.billingForm.value.address } : {}),
          ...(this.billingForm.value.postCode ? { postCode: this.billingForm.value.postCode } : {}),
          ...(this.billingForm.value.owner?.name
            ? {
                owner: {
                  name: this.billingForm.value?.owner?.name,
                  ...(this.billingForm.value.owner?.phone ? { phone: this.billingForm.value?.owner?.phone } : {}),
                },
              }
            : {}),
        },
        ...map(this.selectedBillAddresses, (address) => {
          return {
            ...(address?.country ? { country: address?.country?.id } : {}),
            ...(address?.state ? { state: address?.state?.id } : {}),
            ...(address?.city ? { city: address?.city } : {}),
            ...(address?.address ? { address: address?.address } : {}),
            ...(address?.postCode ? { postCode: address?.postCode } : {}),
            ...(address?.owner?.name
              ? {
                  owner: {
                    name: address?.owner?.name,
                    ...(address?.owner?.phone ? { phone: address?.owner?.phone } : {}),
                  },
                }
              : {}),
          };
        }),
      ];
    }
    input = {
      billingAddress,
    };
    this.updateCurrentUser(input);
  }

  updateCurrentUser(input: any) {
    this.userService
      .updateCurrentUser(input)
      .pipe(
        catchError(() => {
          this.modalServices.dismissAll();
          this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.modalServices.dismissAll();
          this.translate.get('SHARED.CHANGES_SAVED').subscribe((text: string) => {
            this.toastrService.success(text);
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  deleteAddress(index: number, field: string) {
    let billingAddress;
    let shippingAddress;
    let input;
    if (field === 'billingAddress') {
      this.selectedBillAddresses = [...slice(this.selectedBillAddresses, 0, index), ...slice(this.selectedBillAddresses, index + 1)];
      billingAddress = [
        ...map(this.selectedBillAddresses, (address) => {
          return {
            ...(address?.country ? { country: address?.country?.id || address?.country } : {}),
            ...(address?.state ? { state: address?.state?.id || address?.state } : {}),
            ...(address?.city ? { city: address?.city } : {}),
            ...(address?.address ? { address: address?.address } : {}),
            ...(address?.postCode ? { postCode: address?.postCode } : {}),
            ...(address?.owner?.name
              ? {
                  owner: {
                    name: address?.owner?.name,
                    ...(address?.owner?.phone ? { phone: address?.owner?.phone } : {}),
                  },
                }
              : {}),
          };
        }),
      ];
      input = {
        billingAddress,
      };
    } else {
      this.selectedShipAddresses = [...slice(this.selectedShipAddresses, 0, index), ...slice(this.selectedShipAddresses, index + 1)];
      shippingAddress = [
        ...map(this.selectedShipAddresses, (address) => {
          return {
            ...(address?.country ? { country: address?.country?.id || address?.country } : {}),
            ...(address?.state ? { state: address?.state?.id || address?.state } : {}),
            ...(address?.city ? { city: address?.city } : {}),
            ...(address?.address ? { address: address?.address } : {}),
            ...(address?.postCode ? { postCode: address?.postCode } : {}),
            ...(address?.owner?.name
              ? {
                  owner: {
                    name: address?.owner?.name,
                    ...(address?.owner?.phone ? { phone: address?.owner?.phone } : {}),
                  },
                }
              : {}),
          };
        }),
      ];
      input = {
        shippingAddress,
      };
    }
    this.updateCurrentUser(input);
  }

  openBillAddressModal(content: any, billingAddress: FullAddressType, index?: number) {
    this.isBillButtonDisabled = true;
    this.selectedIndex = index;
    this.step = 1;
    this.selectedBillAddress = billingAddress;
    this.modalServices.open(content, { backdrop: false, centered: true, size: 'lg' });
    this.billingForm = this.formBuilder.group({
      address: [this.selectedBillAddress?.address || '', Validators.required],
      country: [this.selectedBillAddress?.country?.id || '', Validators.required],
      state: [this.selectedBillAddress?.state?.id || '', Validators.required],
      postCode: [this.selectedBillAddress?.postCode || '', Validators.required],
      city: [this.selectedBillAddress?.city || '', Validators.required],
      owner: this.formBuilder.group({
        name: [this.selectedBillAddress?.owner?.name || '', Validators.required],
        phone: this.formBuilder.group({
          countryCode: [this.selectedBillAddress?.owner?.phone.countryCode || '216', Validators.required],
          number: [this.selectedBillAddress?.owner?.phone.number || '', Validators.required],
        }),
      }),
    });
    this.initialValues = this.billingForm.value;
    this.billingForm.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((ivalues) => {
      this.isBillButtonDisabled = isEqual(this.initialValues, ivalues);
    });
    this.cords = {
      lng: billingAddress?.location?.coordinates?.[0],
      lat: billingAddress?.location?.coordinates?.[1],
    };
  }

  openShipAddressModal(content: any, shippingAddress: FullAddressType, index?: number) {
    this.isShipButtonDisabled = true;
    this.selectedIndex = index;
    this.step = 1;
    this.selectedShipAddress = shippingAddress;
    this.modalServices.open(content, { backdrop: false, centered: true, size: 'lg' });
    this.shippingForm = this.formBuilder.group({
      address: [this.selectedShipAddress?.address || '', Validators.required],
      country: [this.selectedShipAddress?.country?.id || '', Validators.required],
      state: [this.selectedShipAddress?.state?.id || '', Validators.required],
      postCode: [this.selectedShipAddress?.postCode || '', Validators.required],
      city: [this.selectedShipAddress?.city || '', Validators.required],
      owner: this.formBuilder.group({
        name: [this.selectedShipAddress?.owner?.name || '', Validators.required],
        phone: this.formBuilder.group({
          countryCode: [this.selectedShipAddress?.owner?.phone.countryCode || '216', Validators.required],
          number: [this.selectedShipAddress?.owner?.phone.number || '', Validators.required],
        }),
      }),
    });
    this.initialValues = this.shippingForm.value;
    this.shippingForm.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((ivalues) => {
      this.isShipButtonDisabled = isEqual(this.initialValues, ivalues);
    });
  }

  openOrderModal(content: any, order: MarketPlaceOrderDtoType) {
    this.selectedOrder = order;
    this.modalServices.open(content, { backdrop: false, centered: true, size: 'lg' });
  }

  cancelProduct(orderId: string, barcodeId: string): void {
    this.orderService
      .cancelProduct({ orderId, barcodeId })
      .pipe(
        catchError(() => {
          this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          this.modalServices.dismissAll();
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((res: any) => {
        if (res) {
          this.modalServices.dismissAll();
          this.translate.get('SHARED.PRODUCT_CANCELED_SUCCESS').subscribe((text: string) => {
            this.toastrService.success(text);
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  checkPhone() {
    if (this.userForm.get('phone').value) {
      this.authService
        .isLoginForTargetExist(null, this.userForm.get('phone').value)
        .pipe(take(1))
        .subscribe(({ exist }) => {
          if (exist) {
            this.userForm.get('phone').setErrors({ alreadyExists: exist });
          }
        });
    }
  }

  ValidateEmail() {
    this.appService
      .sendValidationEmailForTarget(this.currentUser.email)
      .pipe(
        catchError(() => {
          this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.translate.get('SHARED.VERIFICATION_EMAIL_SENT').subscribe((text: string) => {
            this.toastrService.success(text);
          });
        }
      });
  }

  pickAddress(event) {
    if (this.field === 'shippingAddress') {
      this.cords = event.coords;
      this.shippingForm.get(['location', 'coordinates']).patchValue([event.coords.lng, event.coords.lat]);
    } else {
      this.cords = event.coords;
      this.billingForm.get(['location', 'coordinates']).patchValue([event.coords.lng, event.coords.lat]);
    }
  }

  next(): void {
    this.step = 2;
  }

  updatePassword() {
    this.isPwdButtonDisabled = true;
    this.authService
      .updateCurrentUserPassword(this.passwordForm.get(['pwd', 'currentPassword']).value, this.passwordForm.get(['pwd', 'password']).value)
      .pipe(
        catchError(() => {
          this.showAlert = true;
          this.alert = {
            message: 'Le mot de passe est incorrect!',
          };
          this.translate.get('SHARED.INCORRECT_PASSWORD').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          this.modalServices.dismissAll();
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.showAlert = false;
          this.modalServices.dismissAll();
          this.translate.get('SHARED.PASSWORD_UPDATED').subscribe((text: string) => {
            this.toastrService.success(text);
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  toggleDetails(orderId: string): void {
    if (this.selectedOrder && this.selectedOrder.id === orderId) {
      this.closeDetails();
      return;
    }
    this.orderService
      .getOrderById(orderId)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((order) => {
        this.selectedOrder = order;
        this.changeDetectorRef.markForCheck();
      });
  }

  closeDetails(): void {
    this.selectedOrder = null;
  }

  cancelProductList(orderId: string): void {
    this.orderService
      .cancelList(orderId)
      .pipe(
        catchError(() => {
          this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.translate.get('SHARED.ORDER_CANCELED').subscribe((text: string) => {
            this.toastrService.success(text);
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  isCancelProducts(order: MarketPlaceOrderDtoType): boolean {
    if (order?.shoppingCart?.products?.length) {
      const productStatuses = map(order.shoppingCart.products, 'orderProductStatus');
      if (productStatuses.includes(OrderStatus.OPEN)) {
        return false;
      }
    }
    return true;
  }

  downloadDocument(order: MarketPlaceOrderDtoType) {
      this.orderService
        .getTargetOrderInvoice(order.id)
        .pipe(
          catchError(() => {
            this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
              this.toastrService.error(text);
            });
            this.changeDetectorRef.markForCheck();
            return of(null);
          }),
        )
        .subscribe((res) => {
          if (res) {
            const blob = this.convertorHelper.b64toBlob(res?.content, 'application/pdf');
            const a = this.document.createElement('a');
            this.document.body.appendChild(a);
            const url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download = String('order');
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
            this.changeDetectorRef.markForCheck();
          }
        });
  }

  findMarketPlaceOrdersByUserAndTarget() {
    this.isLaodingOrders = true;
    this.orderService
      .findMarketPlaceOrdersByUserAndTarget()
      .pipe(
        catchError(() => {
          this.isLaodingOrders = false;
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((res: any) => {
        this.totalCount = res?.count;
        this.isLaodingOrders = false;
        this.changeDetectorRef.markForCheck();
      });
  }

  viewTab($event: Event, prevId: number, nextId: number) {
    $event.preventDefault();
    const nodes = this.el.nativeElement.querySelectorAll('.nav-dashboard .nav-link');
    this.renderer.removeClass(nodes[prevId], 'active');
    this.renderer.addClass(nodes[nextId], 'active');
  }

  async signOut() {
    this.authService
      .signOut()
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  updateUser() {
    this.isButtonDisabled = true;
    const formValue = {
      ...FormHelper.getNonEmptyAndChangedValues(omit(this.userForm.value, 'pwd'), omit(this.initialUserForm, 'pwd')),
    };
    this.userService
      .updateCurrentUser(formValue)
      .pipe(
        catchError(() => {
          this.showAlert = true;
          this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          this.modalServices.dismissAll();
          this.changeDetectorRef.markForCheck();
          return of(null);
        }),
      )
      .subscribe((result) => {
        if (result) {
          this.showAlert = false;
          this.modalServices.dismissAll();
          this.translate.get('SHARED.CHANGES_SAVED').subscribe((text: string) => {
            this.toastrService.success(text);
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }
}
